import { getViewport } from "@/canvasController/helpers";
import { arrowWidget } from "@/elements/arrow";
import { frameWidget } from "@/elements/frame";
import { Group } from "@/group";
import {
    COLORS,
    LOCK_MAX_DPR,
    MIN_RECT_SIZE,
    MIN_VIEWPORT_SIDE,
    VIEWPORT_RADIUS,
} from "@/interface/BottomBar/MiniMap/MiniMapWindow/constants";
import type {
    MiniMapRect,
    MiniMapTransform,
} from "@/interface/BottomBar/MiniMap/MiniMapWindow/types";

export const drawViewportRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) => {
    const r = Math.min(radius, width / 2, height / 2);

    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.arcTo(x + width, y, x + width, y + r, r);
    ctx.lineTo(x + width, y + height - r);
    ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
    ctx.lineTo(x + r, y + height);
    ctx.arcTo(x, y + height, x, y + height - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
};

export const getViewportRadius = (vpWidth: number, vpHeight: number, maxRadius: number) => {
    const minSide = Math.min(vpWidth, vpHeight);

    return Math.max(
        1, // минимальный радиус — чтобы не превращался в квадрат
        Math.min(maxRadius, minSide * 0.2),
    );
};

export const serializeMiniMapToMainCanvCoordinates = (
    x: number,
    y: number,
    transform: MiniMapTransform,
) => ({
    x: (x - transform.offsetX) / transform.scale + transform.sceneStartX,
    y: (y - transform.offsetY) / transform.scale + transform.sceneStartY,
});

export const serializeItemsToMiniMapRects = (
    items: Group[],
    transform: MiniMapTransform,
): MiniMapRect[] => {
    const { scale, offsetX, offsetY, sceneStartX, sceneStartY } = transform;

    return items.reduce<MiniMapRect[]>((acc, item) => {
        if (!(item instanceof Group) || !item.isItemWrapper() || item.children?.length !== 1) {
            return acc;
        }

        const child = item.children[0];

        if (child instanceof arrowWidget) {
            return acc;
        }

        const width = item.width * scale;
        const height = item.height * scale;

        acc.push({
            x: offsetX + (item.startX - sceneStartX) * scale,
            y: offsetY + (item.startY - sceneStartY) * scale,
            width,
            height,
        });

        return acc;
    }, []);
};

export const drawMiniMapItems = (ctx: CanvasRenderingContext2D, rects: MiniMapRect[]) => {
    ctx.save();
    ctx.fillStyle = COLORS.itemFill;

    for (const rect of rects) {
        ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }

    ctx.restore();
};

export const clusterMiniMapRects = (rects: MiniMapRect[], cellSize: number): MiniMapRect[] => {
    const grid = new Map<number, Map<number, MiniMapRect[]>>();

    for (const rect of rects) {
        const cx = Math.floor(rect.x / cellSize);
        const cy = Math.floor(rect.y / cellSize);

        let col = grid.get(cx);
        if (!col) {
            col = new Map();
            grid.set(cx, col);
        }

        let bucket = col.get(cy);
        if (!bucket) {
            bucket = [];
            col.set(cy, bucket);
        }

        bucket.push(rect);
    }

    const result: MiniMapRect[] = [];

    for (const col of grid.values()) {
        for (const bucket of col.values()) {
            if (bucket.length === 1) {
                result.push(bucket[0]);
                continue;
            }

            let minX = Infinity;
            let minY = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;

            for (const r of bucket) {
                minX = Math.min(minX, r.x);
                minY = Math.min(minY, r.y);
                maxX = Math.max(maxX, r.x + r.width);
                maxY = Math.max(maxY, r.y + r.height);
            }

            result.push({
                x: minX,
                y: minY,
                width: maxX - minX,
                height: maxY - minY,
            });
        }
    }

    return result;
};

export const getClusterCellSize = (scale: number): number => {
    const MIN = 1;
    const MAX = 2;
    const cell = 12 / scale;

    return Math.max(MIN, Math.min(MAX, cell));
};

export const computeMiniMapTransform = (
    canvas: HTMLCanvasElement,
    bound: {
        startX: number;
        startY: number;
        width: number;
        height: number;
    },
): MiniMapTransform => {
    const { startX, startY, width, height } = bound;

    const isInvalidScene = width <= 0 || height <= 0 || canvas.width <= 0 || canvas.height <= 0;

    if (isInvalidScene) {
        return {
            scale: 1,
            offsetX: canvas.width / 2,
            offsetY: canvas.height / 2,
            sceneStartX: startX,
            sceneStartY: startY,
            sceneWidth: width,
            sceneHeight: height,
        };
    }

    const scale = Math.min(canvas.width / width, canvas.height / height);

    const mapWidth = width * scale;
    const mapHeight = height * scale;

    const offsetX = (canvas.width - mapWidth) / 2;
    const offsetY = (canvas.height - mapHeight) / 2;

    return {
        scale,
        offsetX,
        offsetY,
        sceneStartX: startX,
        sceneStartY: startY,
        sceneWidth: width,
        sceneHeight: height,
    };
};

export const getVisibleMiniMapRects = (
    items: Group[],
    transform: MiniMapTransform,
): MiniMapRect[] => {
    const miniMapRects = serializeItemsToMiniMapRects(items, transform);

    const shouldCluster = transform.scale < 0.4;
    const cellSize = getClusterCellSize(transform.scale);

    const rects = shouldCluster ? clusterMiniMapRects(miniMapRects, cellSize) : miniMapRects;

    return rects.filter((r) => r.width >= MIN_RECT_SIZE && r.height >= MIN_RECT_SIZE);
};

export const drawViewport = (ctx: CanvasRenderingContext2D, transform: MiniMapTransform) => {
    const { viewportStartX, viewportStartY, viewportWidth, viewportHeight } = getViewport();

    const { offsetX, offsetY, scale, sceneStartX, sceneStartY, sceneWidth, sceneHeight } =
        transform;

    let vpX = offsetX + (viewportStartX - sceneStartX) * scale;
    let vpY = offsetY + (viewportStartY - sceneStartY) * scale;
    let vpWidth = viewportWidth * scale;
    let vpHeight = viewportHeight * scale;

    // Доп логика, чтобы viewport не схлопывался в совсем мелкую точку
    const minSide = Math.min(vpWidth, vpHeight);

    if (minSide < MIN_VIEWPORT_SIDE && minSide > 0) {
        const scaleFactor = MIN_VIEWPORT_SIDE / minSide;
        const newWidth = vpWidth * scaleFactor;
        const newHeight = vpHeight * scaleFactor;

        vpX -= (newWidth - vpWidth) / 2;
        vpY -= (newHeight - vpHeight) / 2;
        vpWidth = newWidth;
        vpHeight = newHeight;
    }

    const radius = getViewportRadius(vpWidth, vpHeight, VIEWPORT_RADIUS);
    drawViewportRect(ctx, vpX, vpY, vpWidth, vpHeight, radius);

    ctx.strokeStyle = COLORS.viewportStroke;
    ctx.stroke();
    const isViewportBiggerThanBound = viewportWidth >= sceneWidth && viewportHeight >= sceneHeight;
    if (!isViewportBiggerThanBound) {
        ctx.fillStyle = COLORS.viewportFill;
        ctx.fill();
    }
};

export const getCappedDPR = () => Math.min(window.devicePixelRatio || 1, LOCK_MAX_DPR);
